import { take, call, put, select, fork, race, takeEvery, Effect } from 'redux-saga/effects';

import { getCurrentUser } from '@/features/Auth/selectors';
import * as api from '../api';
import { Map, List } from 'immutable';
import { filterChangedValues, getMapDiff } from '@/utils/form';
import { IAccount } from '@/types/account';
import { getTypedAccount } from '@/Pages/Account/selectors';
import { push } from '@/App/router/actions';
import { prompt } from '@/components/PromptDialog/sagas';
import { currentUrl } from '@/Main/selectors';
import { id } from '@/types';
import omit from 'lodash/omit';
import i18n from '@/i18n';
import { setCurrentUser } from '@/features/Auth/authSlice';
import {
  STATUS_PAUSED,
  STATUS_WORKING,
  ACTIVATE_ACCOUNT,
  PAUSE_LAST_ADMIN_DIALOG,
  CANCEL_PATCH_USER_STATUS,
} from '../constants';
import { promptRestoreActivity } from '@/utils/status';
import { finishEditUser, setAccountStats } from '@/Pages/Account/actions';
import { IUser, IUserInvite } from '../../../types/user';
import { ICompany } from '@/types/company';
import { showErrorToast, showSuccessToast } from '../../../components/Toastr/actions';
import { findAll, findAllValues } from '../../../models/selectors';
import { IModelsObject } from '../../../models/types';
import { 
  disableReactivateButton,
  enableReactivateButton,
  removeCurrentPageDialog,
  setCurrentPageDialog,
 } from '@/features/PageLayout/pageLayoutSlice';
import { clearEntities } from '../../../models/actions';
import { ADMIN_PERMISSION } from '@/Permissions/knownPermissions';
import { unassignUsersTasks } from '../api';
import { getPauseReasonFromAccountLog } from '@/features/PageLayout/selectors';
import isRtl from '@/utils/isRtl';
// FIX: Strictly use runtime require to break circular dependency loop
function* safeQueryModels(...args: any[]) {
    try {
        // Use Promise-based dynamic import for Webpack 5 to correctly load ESM modules
        // instead of legacy require() which returns an unresolved module object.
        const modelsModule = yield call(() => import('@/models/sagas'));
        
        // Robustly find the function whether it's a named export or default export
        const queryModelsFn = modelsModule.queryModels || (modelsModule.default && modelsModule.default.queryModels);
        
        if (typeof queryModelsFn === 'function') {
            return yield call(queryModelsFn, ...args);
        }
        console.error('[AccountSaga] queryModels function not found in @/models/sagas module:', modelsModule);
        return null;
    } catch (e) {
        console.error('[AccountSaga] Failed to lazy load queryModels', e);
        return null;
    }
}
export function* activateAccountWatcher() {
  yield takeEvery( ACTIVATE_ACCOUNT, function* () {
    yield call(activateAccount, null);
  });
}
export function* performActivation(account: IAccount, restore: boolean | null = null, pushToUrl: boolean = true) {
  yield put(disableReactivateButton());
  if (restore === null) {
    restore = yield call(promptRestoreActivity, 'account', account);
    yield put(enableReactivateButton());
    if (restore === null) {
      return false;
    }
  }
  const updatedAccount = yield call(api.activateAccount, account.id, restore);
  if (!updatedAccount) {
    yield put(enableReactivateButton());
    return false;
  }
  yield put(setCurrentUser(updatedAccount as any));
  const url = yield select(currentUrl());
  if (pushToUrl) {
    yield put(push(url));
  }
  yield put(enableReactivateButton());
  return true;
}
export function* activateAccount(restore: boolean | null = null, pushToUrl: boolean = true) {
  const account: IAccount = yield select(getTypedAccount);
  if (!account || !account.id) {
      console.warn('[AccountSaga] activateAccount: No account found');
      return false;
  }
}
export function* pauseAccount() {
  const account: IAccount = yield select(getTypedAccount);
  if (!account || !account.id) return false;
  const message = i18n.t('account.pause-warning', { ns: 'sagas' });
  const confirmed = yield call(prompt, message, {
    yesLabel: i18n.t('common.pause', { ns: 'sagas' }),
    noLabel: i18n.t('common.cancel', { ns: 'sagas' }),
  });
  if (!confirmed) {
    return false;
  }
  const updatedAccount = yield call(api.pauseAccount, account.id);
  if (!updatedAccount) {
    return false;
  }
  yield put(setCurrentUser(updatedAccount as any));
  return true;
}
export function* patchStatus({ status }: { status: number }) {
  if (status === STATUS_WORKING) {
    yield call(activateAccount);
  } else if (status === STATUS_PAUSED) {
    yield call(pauseAccount);
  }
}
export function* fetchCurrentAccountWithRelations(includes: any = {}) {
  const currentAccount: IAccount = yield select(getTypedAccount);
  if (!currentAccount || !currentAccount.id) return;
  const entities = yield call(
    safeQueryModels,
    'account',
    [currentAccount.id],
    [
      {
        relation: 'companies',
        scope: {
          include: [].concat(includes.company ? includes.company : []),
        },
      },
      {
        relation: 'cards',
        scope: {
          include: [].concat(includes.card ? includes.card : []),
        },
      },
      {
        relation: 'billingThreshold',
      },
      {
        relation: 'accountLogs',
        order: 'createdAt DESC',
        limit: 1,
        where: {
          event: 'pause',
        },
      },
    ].concat(includes.account ? includes.account : []),
  );
  // FIX: Guard against null entities returned by safeQueryModels crash
  if (!entities || !entities.account) {
      // It is normal to fail if queryModels crashes, but we shouldn't crash the saga
      return;
  }
  const { account } = entities;
  if (account && account.length > 0) {
      yield put(setCurrentUser(account[0] as any));
  }
  return entities;
}
function* fetchAccountAndBilling(id: id) {
  yield call(safeQueryModels, 'account', [id], [
    {
      relation: 'billingThreshold',
    },
    {
      relation: 'bill',
    },
  ]);
}
export function* fetchAccountAndBillingInfo() {
  const currentAccount: IAccount = yield select(getTypedAccount);
  if (!currentAccount || !currentAccount.id) {
      return;
  }
  yield put(clearEntities(['billingThreshold', 'bill']));
  yield call(fetchAccountAndBilling, currentAccount.id);
}
function* fetchAccountLastLog(id: id) {
  yield call(safeQueryModels, 'account', [id], [
    {
      relation: 'accountLogs',
      order: 'createdAt DESC',
      limit: 1,
      where: {
        event: 'pause',
      },
    },
  ]);
}
export function* fetchAccountLog() {
  const currentAccount: IAccount = yield select(getTypedAccount);
  if (!currentAccount || !currentAccount.id) return;
  yield call(fetchAccountLastLog, currentAccount.id);
}
export function* editAccountAndMainUser(values: Map<any, any>, currentAccount: IAccount) {
  const currentUser: IUser = yield select(getCurrentUser);
  const valuesWithMainUser = (values as any).get('myAccount')
    ? values.set('mainUserId', currentUser.id)
    : values.set('mainUserId', null);
  const patchedAccount = yield call(editAccount, valuesWithMainUser, currentAccount);
  return patchedAccount;
}
export function* editAccount(values: Map<any, any>, currentAccount: IAccount) {
  const filteredValues = filterChangedValues(
    
    values.delete('newmainLocation'),
    Map(currentAccount),
  );
  if (!filteredValues.isEmpty()) {
    const patchedAccount = yield call(api.patchAccount, currentAccount.id, filteredValues.toJS());
    return patchedAccount;
  }
}
export function* fetchUsers(accountId: string, includes: any = {}) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { user } = yield call(safeQueryModels, 'account', [accountId], [
    {
      relation: 'users',
      scope: {
        include: includes.users || [],
      },
    },
  ]);
}
export function* fetchAccountStats() {
  const account: IAccount = yield select(getTypedAccount);
  if (!account || !account.id) return;
  const stats = yield call(api.fetchAccountStats as any, account.id);
  yield put(setAccountStats(stats));
}
export function* inviteTeamMembers({ users }: { users: IUserInvite[] }) {
  try {
    const invitedUsers: IUser[] = yield call(api.inviteTeamMembers, users);
    return invitedUsers;
  } catch (err) {
    yield put(showErrorToast((err as Error).message));
    throw err;
  }
}
export function* fetchTeamMember(userId: string) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const entities: IModelsObject = yield call(safeQueryModels, 'user', [userId], []);
}
export function* patchUser({ userId, data }: { userId: id; data: any }) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const patchedUser = yield call(api.patchUser, userId, omit(data, ['address', 'location', 'assignedCompanies']));
  yield put(finishEditUser());
}
export function* patchUserStatus(
  reload: (id: string) => IterableIterator<Effect>,
  { userId, status }: { userId: string; status: number },
) {
  const users: IUser[] = yield select(findAllValues('user'));
  const user = users.find((u) => u.id === userId);
  if (!user || status === user.status) {
    return;
  }
  if (status === STATUS_PAUSED) {
    const team: IUser[] = yield select(findAllValues('user'));
    const admins = team.filter(
      (u) => u.status !== STATUS_PAUSED && u.permission === ADMIN_PERMISSION && u.id !== userId,
    );
    if (!admins.length) {
      yield put(setCurrentPageDialog({ dialog: PAUSE_LAST_ADMIN_DIALOG }));
      yield take(CANCEL_PATCH_USER_STATUS);
      yield put(removeCurrentPageDialog(PAUSE_LAST_ADMIN_DIALOG));
      return;
    }
    const companies: ICompany[] = yield select(findAllValues('company'));
    const companyIds = companies.map((c) => c.id);
    
  }
  yield call(api.patchUser, userId, { status });
}
